import React, { useState } from 'react'
import { useMediaQuery } from '../../../hooks/useMediaQuery'
import Image from '../../1_Atoms/Image/Image'
import Select from '../../1_Atoms/Forms/Select/Select'
import { languageController } from '../../../controllers/language/languageController'
import Link from '../../1_Atoms/Link/Link'
import generalController from '../../../controllers/generalController'
import { getBaseUrl } from '../../../utils/helpers'
import ReactHtmlParser from 'react-html-parser';

interface TrimOption {
  slug: string;
  trim: string;
  name: string;
}

interface ModelYearData {
  jelly_path: string;
  jelly: string;
}

interface YearData {
  url_slug: string;
}

interface TrimSelectorProps {
  index: number;
  defaultTrim: string;
  trim: string;
  trimOptions: TrimOption[];
  trimPrice: string;
  modelYearData: ModelYearData;
  yearData: YearData;
  modelName: string;
  updateTrimsOrder: () => void;
  showDesignYoursButton: boolean;
}

const TrimSelector: React.FC<TrimSelectorProps> = ({
  index,
  defaultTrim,
  trim,
  trimOptions,
  trimPrice,
  modelYearData,
  yearData,
  modelName,
  updateTrimsOrder,
  showDesignYoursButton
}) => {
  const [activeTrim, setActiveTrim] = useState<string>(defaultTrim)
  const isMobile = ['xs', 'sm'].includes(useMediaQuery() || '');

  const displayType = !isMobile && index == 0 ? 'flex' : 'inherit';

  const handleSelectorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    window.location.href = window.location.origin + (window.location.pathname || '') + '?trim=' + value;
  }
  // Generate an image path for jelly.
  const generateImageSrc = (modelYearData: ModelYearData): string => {
    const { jelly_path, jelly } = modelYearData;
    const imagePath = getBaseUrl() + process.env.REACT_APP_BASIC_CONTENT_PATH;
    return imagePath + jelly_path + jelly;
  }

  const generateBnPLink = (trimCode: string): string => {
  	const lang = generalController.detectLanguage();
    const basePath = lang === 'en' ? '/shopping/build-and-price/#/' : '/magasiner/construction-et-prix/#/';
    const trimParams = `${yearData.url_slug}/${modelName}/${trimCode}`;

    if (basePath) {
      return '/' + lang + basePath + trimParams;
    }
    return ''
  }

  const getTrimWithLegalSup = () => {
    let sup = '';

    if (modelName === 'CX-50-HEV' && trim == 'GS-L' && yearData.url_slug === '2025') {
      sup = '<sup>†</sup>';
    }

    
    if (modelName === 'MX-5-SoftTop' && trim == '35th Anniversary Edition' && yearData.url_slug === '2025') {
      trim = trim.replace('th', '<sup>th</sup>');
      sup = '<sup>†3</sup>';
    }

    if (modelName === 'MX-5-SoftTop' && trim == '35e anniversaire' && yearData.url_slug === '2025') {
      trim = trim.replace('35e', '35<sup>e</sup>');
      sup = '<sup>†3</sup>';
    }

    return ReactHtmlParser(`${trim}${sup}`);
  }
  
  const convertSupToUnicode = (htmlString: string): string => {
    // Map for superscript replacements
    const superscriptMap: { [key: string]: string } = {
        '0': '⁰',
        '1': '¹',
        '2': '²',
        '3': '³',
        '4': '⁴',
        '5': '⁵',
        '6': '⁶',
        '7': '⁷',
        '8': '⁸',
        '9': '⁹',
        'a': 'ᵃ',
        'b': 'ᵇ',
        'c': 'ᶜ',
        'd': 'ᵈ',
        'e': 'ᵉ',
        'f': 'ᶠ',
        'g': 'ᵍ',
        'h': 'ʰ',
        'i': 'ⁱ',
        'j': 'ʲ',
        'k': 'ᵏ',
        'l': 'ˡ',
        'm': 'ᵐ',
        'n': 'ⁿ',
        'o': 'ᵒ',
        'p': 'ᵖ',
        'r': 'ʳ',
        's': 'ˢ',
        't': 'ᵗ',
        'u': 'ᵘ',
        'v': 'ᵛ',
        'w': 'ʷ',
        'x': 'ˣ',
        'y': 'ʸ',
        'z': 'ᶻ',
        '+': '⁺',
        '-': '⁻',
        '=': '⁼',
        '(': '⁽',
        ')': '⁾',
    };

    // Replace <sup> tags and convert their contents to Unicode
    return htmlString.replace(/<sup>(.*?)<\/sup>/g, (_, content) => {
        // Convert each character inside <sup> to its Unicode equivalent
        return [...content].map(char => superscriptMap[char] || char).join('');
    });
}

  
  return (
    <td className={index == 0 ? 'mz-table-corner mz-table-560 mz-table-tablet-350 mz-table-mobile-150': ' text--center mz-table-250 mz-table-tablet-150 mz-table-mobile-150' }>
      <div style={{display: displayType, alignItems:'center'}}>
        <Image
          className="mz-trim-selector__image"
          style={{flex:'0 0 60%'}}
          src={generateImageSrc(modelYearData)}
          isLazy={false}
        />
        <p style={{flex:'0 0 40%'}}>
          <strong>{getTrimWithLegalSup()}</strong><br/>
          {showDesignYoursButton && (
          <a
            href={generateBnPLink(activeTrim)}
            className="mz-link mz-trim-selector__cta"
          >{languageController.getCopy('specCompare', 'trimColumnCTALabel')}</a>)}
          {index == 0 &&
          <div className="mz-form-item mz-form-item--select ">
            <select className="mz-form-item__select" onChange={handleSelectorChange}>
              <option value="" disabled>{languageController.getCopy('specCompare', 'changeTrim')}</option>
              {
                (
                  trimOptions.map((option, idx) => {
                    if(trim !== option.slug){
                      return (<option key={idx} value={option.trim}>{convertSupToUnicode(option.name)}</option>)
                    }
                    return null;
                  })
                )
              }
            </select>
            <div className="mz-form-item__arrow-down">
              <div className="mz-svg-wrapper " style={{fontSize: '0.75rem'}}>
                <i className="mz-svg mz-svg--grey undefined mz-svg-chevron_small_down"></i>
              </div>
            </div>

          </div>
          }
        </p>
      </div>
    </td>
  )
}

export default TrimSelector